/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Compat-слой роутинга (Фазы 2–5, docs/MIGRATION_ROUTER.md).
 *
 * Единая точка импорта роутер-API для ВСЕГО приложения:
 * `import { useHistory, Link, ... } from '@compat/router'`.
 *
 * С Фазы 5 react-router-dom и history УДАЛЕНЫ из зависимостей: слой
 * больше не детектит «зону» (__RouterContext исчез вместе с RRv5-хостом
 * в волне E) и всегда работает поверх TanStack Router:
 *  - URL-состояние: legacyHistory (v4-адаптер над router.history);
 *  - матчинг v5-путей (':id(\d+)', '/shop-:shopId', массивы,
 *    exact/strict/sensitive): собственный порт matchPath на
 *    path-to-regexp@1 (см. ./matchPath.ts) — тот же движок, что был
 *    внутри v5;
 *  - типы History/Location/match/RouteProps/... — собственные
 *    (см. ./types.ts).
 *
 * Полный уход от этого слоя (useHistory → useNavigate и типизированные
 * Link) — отдельный codemod ПОСЛЕ стабилизации, без спешки.
 */
import type {
  ComponentType,
  MouseEvent,
  ReactElement,
  ReactNode
} from 'react';
import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react';
import {
  useLocation as useTanLocation,
  useParams as useTanParams,
  useRouterState
} from '@tanstack/react-router';
import { matchPath, generatePath } from './matchPath';
import type {
  History,
  Location,
  LocationDescriptor,
  match as Match
} from './types';
import { legacyHistory } from '../legacyHistory';

// ---------------------------------------------------------------------------
// Хуки
// ---------------------------------------------------------------------------

/**
 * Подписка на смену URL: select по href — минимальный ре-рендер только
 * при реальной смене адреса.
 */
const useUrlSubscription = (): string =>
  useTanLocation({ select: l => l.href });

/**
 * V5-атомарность (БАГФИКС 08.08.2026, репорт Sergei: back из рецепта в
 * /recipes/r/... уводил на /404). У TanStack переход асинхронный
 * (лоадеры, чанки autoCodeSplitting): state.location уже НОВЫЙ, а
 * СТАРОЕ дерево ещё смонтировано и ре-рендерится. В v5 Switch менял
 * location и компонент атомарно — расхождения пары не существовало.
 * Здесь же старый Route пересчитывал match по новому URL: не-exact
 * '/recipes/:id' матчил '/recipes/r/bludo/23' с id='r' → NaN-запросы
 * ($id null, excludeIds[0] null) и replace('/404') из useRecipeParsedId.
 * Лечение: пока роутер не idle, location для СМОНТИРОВАННОГО дерева
 * заморожен (последний закоммиченный); свежий маунт берёт живой URL.
 * Проверено стендом smoke11 (/tmp/rt-smoke) + smoke8 (семантика compat).
 */
const useCompatLocation = (): Location<any> => {
  const href = useUrlSubscription();
  const isIdle = useRouterState({ select: s => s.status === 'idle' });
  const ref = useRef<{ href: string; location: Location<any> } | null>(null);
  if (ref.current === null || (isIdle && ref.current.href !== href)) {
    ref.current = { href, location: legacyHistory.location };
  }
  return ref.current.location;
};

/**
 * Матч ближайшего универсального Route — эмуляция RouterContext v5
 * (волна D): useParams/useRouteMatch внутри вложенного Switch
 * (PersonalArea: /cabinet/orders/:id, Catalog и т.п.) должны видеть
 * параметры ЭТОГО Route, которых нет в параметрах файл-роута TanStack
 * (у сплата $ параметр только _splat).
 */
const CompatMatchContext = createContext<Match<any> | null>(null);

export function useHistory<S = any>(): History<S> {
  // подписка нужна: код вида `useHistory().location.pathname` в рендере
  // опирается на ре-рендер при смене URL (в v5 это делал контекст)
  useUrlSubscription();
  return legacyHistory as unknown as History<S>;
}

export function useLocation<S = any>(): Location<S> {
  return useCompatLocation() as Location<S>;
}

// Дженерик-констрейнты 1:1 с бывшим @types/react-router — иначе
// интерфейсы параметров без индекс-сигнатуры (BarMenuUrlParams) не проходят
export function useParams<
  P extends { [K in keyof P]?: string } = Record<string, string>
>(): P {
  const compatMatch = useContext(CompatMatchContext);
  const tanParams = useTanParams({ strict: false });
  // ближайший универсальный Route (вложенный Switch) важнее файл-роута
  return (compatMatch ? compatMatch.params : tanParams) as P;
}

// Оверлоады как у RR: без аргумента match не-nullable
export function useRouteMatch<
  P extends { [K in keyof P]?: string } = Record<string, string>
>(): Match<P>;
export function useRouteMatch<
  P extends { [K in keyof P]?: string } = Record<string, string>
>(path: string | string[] | { path?: string | string[] }): Match<P> | null;
export function useRouteMatch<
  P extends { [K in keyof P]?: string } = Record<string, string>
>(path?: string | string[] | { path?: string | string[] }): Match<P> | null {
  const compatMatch = useContext(CompatMatchContext);
  const compatLocation = useCompatLocation();
  const tanParams = useTanParams({ strict: false });
  const pathKey = JSON.stringify(path ?? null);

  return useMemo(() => {
    const pathname = compatLocation.pathname;
    if (path) {
      return matchPath<P>(pathname, path as any);
    }
    // Без аргумента: матч ближайшего универсального Route (вложенный
    // Switch), иначе — текущего файл-роута TanStack
    if (compatMatch) {
      return compatMatch as Match<P>;
    }
    return {
      path: pathname,
      url: pathname,
      params: tanParams as P,
      isExact: true
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compatMatch, compatLocation, pathKey, tanParams]);
}

// ---------------------------------------------------------------------------
// Компоненты
// ---------------------------------------------------------------------------

interface CompatLinkProps {
  to:
    | LocationDescriptor<any>
    | ((loc: Location<any>) => LocationDescriptor<any>);
  replace?: boolean;
  innerRef?: any;
  component?: ComponentType<any>;
  children?: ReactNode;
  target?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  [key: string]: any;
}

const isModifiedEvent = (e: MouseEvent): boolean =>
  e.metaKey || e.altKey || e.ctrlKey || e.shiftKey;

/**
 * <a> поверх legacyHistory с v4-семантикой RR-Link (объектный to,
 * hash-only to, state, target/_blank, модификаторы клика).
 */
export const Link = forwardRef<HTMLAnchorElement, CompatLinkProps>(
  (props, ref) => {
    const {
      to,
      replace,
      innerRef,
      // component RR-Link'а не поддерживаем (в проекте не используется)
      component: _component,
      children,
      target,
      onClick,
      ...rest
    } = props;
    const resolvedTo =
      typeof to === 'function' ? to(legacyHistory.location) : to;
    const href = legacyHistory.createHref(resolvedTo as any);

    const handleClick = (e: MouseEvent<HTMLAnchorElement>): void => {
      onClick?.(e);
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        (target && target !== '_self') ||
        isModifiedEvent(e)
      ) {
        return;
      }
      e.preventDefault();
      if (replace) {
        legacyHistory.replace(resolvedTo as any);
      } else {
        legacyHistory.push(resolvedTo as any);
      }
    };

    return (
      <a
        ref={(ref ?? innerRef) as any}
        href={href}
        target={target}
        onClick={handleClick}
        {...rest}
      >
        {children}
      </a>
    );
  }
);
Link.displayName = 'CompatLink';

interface CompatNavLinkProps extends CompatLinkProps {
  activeClassName?: string;
  exact?: boolean;
  strict?: boolean;
  isActive?: (m: Match<any> | null, loc: Location<any>) => boolean;
  className?: string;
}

/**
 * NavLink v5 поверх compat-Link (в проекте сейчас не используется —
 * оставлен для полноты API: activeClassName по matchPath текущего URL).
 */
export const NavLink = forwardRef<HTMLAnchorElement, CompatNavLinkProps>(
  (props, ref) => {
    const {
      activeClassName = 'active',
      exact,
      strict,
      isActive,
      className,
      to,
      ...rest
    } = props;
    const location = useLocation();
    const resolvedTo =
      typeof to === 'function' ? to(legacyHistory.location) : to;
    const path =
      typeof resolvedTo === 'string'
        ? resolvedTo.split('#')[0].split('?')[0]
        : resolvedTo.pathname ?? '';
    const routeMatch = path
      ? matchPath(location.pathname, { path, exact, strict })
      : null;
    const active = isActive
      ? isActive(routeMatch, location)
      : Boolean(routeMatch);

    return (
      <Link
        ref={ref}
        to={to}
        className={
          active
            ? `${className ?? ''} ${activeClassName}`.trim()
            : className
        }
        aria-current={active ? 'page' : undefined}
        {...rest}
      />
    );
  }
);
NavLink.displayName = 'CompatNavLink';

interface CompatRedirectProps {
  to: LocationDescriptor<any>;
  push?: boolean;
  from?: string;
  exact?: boolean;
  strict?: boolean;
}

/** Императивный replace/push на маунте — как RR Redirect. */
export const Redirect = (props: CompatRedirectProps): ReactElement | null => {
  const { to, push: isPush } = props;

  useEffect(() => {
    if (isPush) {
      legacyHistory.push(to as any);
    } else {
      legacyHistory.replace(to as any);
    }
    // как и RR Redirect — срабатывает на маунте
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

/** withRouter поверх compat-хуков. */
export function withRouter<P extends object>(
  Component: ComponentType<P>
): ComponentType<Omit<P, 'history' | 'location' | 'match'>> {
  const Wrapped = (props: any): any => {
    const history = useHistory();
    const location = useLocation();
    const routeMatch = useRouteMatch();
    return (
      <Component
        {...props}
        history={history}
        location={location}
        match={routeMatch}
      />
    );
  };
  Wrapped.displayName = `withRouter(${Component.displayName ?? Component.name ?? 'Component'})`;
  return Wrapped;
}

interface CompatRouteProps {
  // как у RouteProps v5 (readonly-массив!)
  path?: string | readonly string[];
  exact?: boolean;
  strict?: boolean;
  sensitive?: boolean;
  component?: ComponentType<any>;
  render?: (props: any) => ReactNode;
  children?: ReactNode | ((props: any) => ReactNode);
  location?: Partial<Location<any>>;
  [key: string]: any;
}

/**
 * Route с эмуляцией v5-семантики (Фаза 4): children-функция вызывается
 * всегда; children-элемент рендерится всегда; component/render — только
 * при матче path против текущего URL. computedMatch от Switch имеет
 * приоритет. Отдаёт свой матч вниз через CompatMatchContext (аналог
 * RouterContext.Provider v5).
 */
export const Route = (props: CompatRouteProps): ReactElement | null => {
  const compatLocation = useCompatLocation();

  const {
    path,
    exact,
    strict,
    sensitive,
    component: Component,
    render,
    children
  } = props;
  const location = (props.location ?? compatLocation) as Location<any>;
  const pathname = location.pathname;

  const routeMatch =
    props.computedMatch ??
    (path
      ? matchPath(pathname, { path, exact, strict, sensitive })
      : // v5 computeRootMatch: Route без path матчится всегда
        { path: '/', url: '/', params: {}, isExact: pathname === '/' });

  const routeProps = {
    history: legacyHistory,
    location,
    match: routeMatch,
    staticContext: undefined
  };

  const provide = (node: ReactNode): ReactElement | null =>
    node === null || node === undefined ? null : (
      <CompatMatchContext.Provider value={routeMatch}>
        {node}
      </CompatMatchContext.Provider>
    );

  if (typeof children === 'function') {
    return provide(children(routeProps));
  }
  if (children !== undefined && children !== null) {
    return provide(children);
  }
  if (routeMatch && Component) {
    return provide(<Component {...routeProps} />);
  }
  if (routeMatch && render) {
    return provide(render(routeProps));
  }
  return null;
};

interface CompatSwitchProps {
  children?: ReactNode;
  location?: Partial<Location<any>>;
}

/**
 * Switch с эмуляцией v5 (Фаза 4, волна D): первый ребёнок, чей
 * path/from матчится, рендерится с computedMatch (его подхватывает
 * наш Route).
 */
export const Switch = (props: CompatSwitchProps): ReactElement | null => {
  const compatLocation = useCompatLocation();

  const location = (props.location ?? compatLocation) as Location<any>;
  let element: ReactElement | null = null;
  let computedMatch: unknown = null;

  Children.forEach(props.children, child => {
    if (computedMatch == null && isValidElement(child)) {
      const childProps = child.props as CompatRouteProps & { from?: string };
      const path = childProps.path ?? childProps.from;
      element = child;
      computedMatch = path
        ? matchPath(location.pathname, {
            path,
            exact: childProps.exact,
            strict: childProps.strict,
            sensitive: childProps.sensitive
          })
        : {
            path: '/',
            url: '/',
            params: {},
            isExact: location.pathname === '/'
          };
    }
  });

  return computedMatch && element
    ? cloneElement(element, { location, computedMatch } as any)
    : null;
};

// ---------------------------------------------------------------------------
// Реэкспорты
// ---------------------------------------------------------------------------
export { matchPath, generatePath };

// Типы (для `import type { ... } from '@compat/router'`)
export type {
  History,
  Location,
  LocationDescriptor,
  Action,
  RouteProps,
  RouteComponentProps,
  match
} from './types';
export type LinkProps = CompatLinkProps;
export type RedirectProps = CompatRedirectProps;
